<template>
  <div class="volume-seek-bar-parts" ref="getBarWidth">
    <progress
      class="volume-seek-bar-parts__progress-bar"
      :max="progressValue.volumeLength"
      :value="progressValue.currentVolume"
    />

    <input
      class="volume-seek-bar-parts__range"
      type="range"
      min="0"
      :max="progressValue.volumeLength"
      v-model.number="seekBarValue"
      @touchstart="handleTouchStartRange"
      @touchend="handleTouchEndRange"
      @mousedown="handleTouchStartRange"
      @mouseup="handleTouchEndRange"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive, ref } from '@vue/composition-api'

export type VolumeDataType = {
  /**
   * 現在の音量
   */
  currentVolume: number
  /**
   * 最大音量
   */
  volumeLength: number
  /**
   * 最大音量
   */
  muted: boolean
  /**
   * ミュート前の音量保持
   */
  tempVolume: number
}

/**
 * レース動画再生画面 動画プレーヤー 音量調整シークバーコンポーネント
 *
 * ※動画再生時間シークバーを流用して作成
 * src/components/RaceVideoPage/RaceVideoPane/parts/SeekBarParts.vue
 * ブラウザー版のみの対応のためにinputからタッチイベントだけ外してfunctionは残しています。
 */
export default defineComponent({
  name: 'VolumeSeekBarParts',
  props: {
    /**
     * シークバー用再生時間データ
     */
    value: {
      type: Object as PropType<VolumeDataType>,
      required: true,
    },
  },
  setup(props, context) {
    const progressValue = reactive(props.value)
    const currentVolume = ref(0)

    const seekBarValue = computed({
      get: () => progressValue.currentVolume,
      set: (val: number) => {
        progressValue.currentVolume = val
        currentVolume.value = val
        context.emit(
          'input',
          Object.assign(props.value, {
            currentVolume: val,
            mute: progressValue.currentVolume === 0,
          }),
        )
      },
    })
    const currentVolumeVisible = ref(false)

    return {
      progressValue,
      seekBarValue,
      currentVolume,
      currentVolumeVisible,
    }
  },
  methods: {
    /**
     * シークバー touchStart時 イベント
     */
    handleTouchStartRange(): void {
      this.$emit('handleTouchStartSeekBar')
    },

    /**
     * シークバー touchEnd時 イベント
     */
    handleTouchEndRange(): void {
      this.$emit('handleTouchEndSeekBar')
    },
  },
})
</script>

<style lang="scss" scoped>
.volume-seek-bar-parts {
  position: relative;
  width: 80px;
  min-width: 80px;
  height: 2px;

  @media screen and (max-width: 920px) {
    width: 40px;
    min-width: 40px;
  }

  @media screen and (max-width: 700px) {
    width: 70px;
    min-width: 70px;
  }

  /* 操作時にちらつくのでハイライト除去 */
  -webkit-tap-highlight-color: transparent;

  &__progress-bar {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    -webkit-appearance: none;

    &::-webkit-progress-bar {
      background: rgba(255, 255, 255, 0.3);
    }

    &::-webkit-progress-value {
      background: $white;
    }
  }

  &__range {
    position: absolute;
    padding: 15px 5px 10px;
    left: -5px;
    top: -14px;
    width: calc(100% + 10px);
    height: 2px;
    background: none;
    will-change: transform;

    &:focus,
    &:active {
      outline: none;
    }

    &::-webkit-slider-thumb {
      display: block;
      width: 14px;
      height: 14px;
      background-color: $white;
      border-radius: 50%;
      -webkit-appearance: none;
      cursor: pointer;

      /* サムのタッチ範囲拡張 */
      box-shadow: none;
      background-clip: padding-box;
      border: 3.5px solid transparent;
      transform: scale(2);
    }
  }
}
</style>

<style lang="scss">
.race-video-pane {
  &--landscape-primary,
  &--landscape-secondary {
    .volume-seek-bar-parts__current-time {
      bottom: 22px;
    }

    .volume-seek-bar-parts__progress-bar {
      background: rgba(#000, 0.7);
    }

    /* フルスクリーン時にタッチ範囲拡張 */
    .volume-seek-bar-parts__range {
      padding-bottom: 15px;

      &::-webkit-slider-thumb {
        border: 4.7px solid transparent;
        transform: scale(3);
      }
    }
  }
}
</style>
